import { useMemo } from "react"

import { TickRendererProps } from "@visx/axis"
import { Text } from "@visx/text"

import { CardBody, CardWrapper } from "@/components/Card"
import { GraphLabel, GraphLabels } from "@/components/graphs/GraphLabels"
import { MultiGraph } from "@/components/graphs/types/bar-line/MultiGraph"
import { Heading } from "@/components/Typography"
import { useLang } from "@/context/useLang"
import { METABASE_EXPECTED_VS_ACTUAL_URL } from "@/constants/metabase"
import { useTrans } from "@/lib/i18n"
import { useMetabasePublicQuestion } from "@/lib/metabase"

interface ExpectedVsActualRow {
	calendar_year: number
	actual_interest: number | null
	actual_capital_opening: number | null
	actual_interest_rate_pct: number | null
	expected_interest: number | null
	expected_capital_opening: number | null
	expected_interest_rate_pct: number | null
}

export function ExpectedVsActualGraph() {
	const t = useTrans("dashboard")
	const { formatNumber } = useLang()

	const { rows, isLoading } = useMetabasePublicQuestion<ExpectedVsActualRow>(
		METABASE_EXPECTED_VS_ACTUAL_URL,
	)

	// MultiGraph aligns grouped bar series by index, so both series must be
	// built from the same `rows` array (no per-series filtering). Missing
	// values render as a zero-height bar.
	const barDatas = useMemo(
		() => [
			{
				id: "actual_interest_rate",
				variant: "primary" as const,
				data: rows.map((row) => ({
					x: row.calendar_year.toString(),
					y: Number(row.actual_interest_rate_pct ?? 0),
				})),
			},
			{
				id: "expected_interest_rate",
				variant: "default" as const,
				data: rows.map((row) => ({
					x: row.calendar_year.toString(),
					y: Number(row.expected_interest_rate_pct ?? 0),
				})),
			},
		],
		[rows],
	)

	const prepareTooltipValues = useMemo(
		() => (year: string) => {
			const row = rows.find((r) => r.calendar_year.toString() === year)
			if (!row) return []

			const formatPct = (v: number | null) =>
				v === null
					? null
					: `${formatNumber(Number(v), { maximumFractionDigits: 2 })}%`

			return [
				{
					id: "actual_interest_rate",
					value: formatPct(row.actual_interest_rate_pct),
					variant: "primary" as const,
					colour: MultiGraph.barVariantColourMap.primary,
				},
				{
					id: "expected_interest_rate",
					value: formatPct(row.expected_interest_rate_pct),
					variant: "default" as const,
					colour: MultiGraph.barVariantColourMap.default,
				},
			]
		},
		[rows, formatNumber],
	)

	const prepareTooltipLabel = useMemo(() => (year: string) => year, [])

	const YearTickComponent = useMemo(
		() =>
			function YearTick({ formattedValue, ...props }: TickRendererProps) {
				return (
					<Text {...props}>
						{(formattedValue ?? "").replace(/[,\s]/g, "")}
					</Text>
				)
			},
		[],
	)

	const PercentTickComponent = useMemo(
		() =>
			function PercentTick({
				formattedValue,
				...props
			}: TickRendererProps) {
				return (
					<Text {...props}>{`${parseFloat(
						formattedValue as string,
					).toFixed(0)}%`}</Text>
				)
			},
		[],
	)

	return (
		<CardWrapper className="mb-4">
			<CardBody>
				<Heading as="h2" styleAs="h3" className="mb-6">
					{t(
						"dashboard:statistics.financial_performance.expected_vs_actual.title",
					)}
				</Heading>
			</CardBody>
			<div className="h-96 w-full">
				{isLoading ? (
					<div className="flex size-full items-center justify-center">
						<div className="size-8 animate-spin rounded-full border-b-2 border-blue-600" />
					</div>
				) : (
					<MultiGraph
						barDatas={barDatas}
						groupedBars
						prepareTooltipValues={prepareTooltipValues}
						prepareTooltipLabel={prepareTooltipLabel}
						// MultiGraph naming: `yTickComponent` formats the x-axis
						// (years), `xTickComponent` the left value axis. Same
						// convention as FinancialPerformanceGraph.
						yTickComponent={YearTickComponent}
						xTickComponent={PercentTickComponent}
					/>
				)}
			</div>
			<GraphLabels className="p-3 px-4 sm:p-5 sm:px-6">
				<GraphLabel
					type="bar"
					variant="primary"
					title={t(
						"dashboard:statistics.financial_performance.expected_vs_actual.actual",
					)}
				/>
				<GraphLabel
					type="bar"
					variant="default"
					title={t(
						"dashboard:statistics.financial_performance.expected_vs_actual.expected",
					)}
				/>
			</GraphLabels>
			<CardBody className="flex flex-col items-center gap-4 text-center">
				<p>
					{t(
						"dashboard:statistics.financial_performance.expected_vs_actual.description",
					)}
				</p>
			</CardBody>
		</CardWrapper>
	)
}
